import { useEffect, useRef, useState } from 'preact/hooks';
import type { RefObject } from 'preact';
import { getRecordFileUrl } from '../lib/api';
import './RecordMedia.css';

// A12 — THE TAPE PLAYS. Small consumers of the one serving door
// (GET /records/:id/files/:field?url=1), one per medium, because the media
// have OPPOSITE constraints:
//
//   · VIDEO and AUDIO point the tag straight at the answered URL. Never
//     fetch→blob for av — a blob: URL needs the whole file before anything
//     plays and kills Range/seeking, which is what the presigned redirect
//     exists to provide (FIX 1). A media tag needs no CORS to play.
//   · IMAGES point the tag straight at the answered URL too, as of
//     2026-09-20 — a plain <img src>, not a fetch-to-blob. (This comment
//     used to say images fetch the URL bare and paint a blob; that stopped
//     being true the day RecordImage changed, and this file is the exact
//     thing the house rule "a code comment that names a rule goes stale
//     silently" warns about, so: see RecordImage's own comment for why.)
//
// ⚑ THE FIELD NAME NAMES INTENT, NOT THE BYTES. `original_file_ref` holds a
// self-tape's video AND a headshot's AND a photo's image AND a reel's video
// — `aa_media.kind` is what a HOUSEHOLD calls the row, and it is not
// authoritative about what the FILE actually is (a household surface like
// HouseholdTapes.tsx deliberately lists every kind of media together — see
// its own header — so a caller that does not already know the kind from
// context must ask the door what the bytes are and render THAT, never guess
// from the field or screen name. `RecordMedia` below is the answer; a caller
// that already knows for certain what a field holds (a review's audio_ref,
// a project's poster_ref) may still use the specific tag directly.
//
// The URL is short-lived under r2 (five minutes), so it is asked for at
// mount, per element — never cached at module level.

function useRecordFileUrl(
  tenantId: string,
  recordId: string,
  fieldName: string,
  playable: boolean
): { src: string | null; mimeType: string | null; failed: boolean } {
  const [state, setState] = useState<{ src: string | null; mimeType: string | null; failed: boolean }>({
    src: null,
    mimeType: null,
    failed: false,
  });
  useEffect(() => {
    let cancelled = false;
    setState({ src: null, mimeType: null, failed: false });
    void getRecordFileUrl(tenantId, recordId, fieldName, { playable })
      .then((f) => {
        if (!cancelled) setState({ src: f.url, mimeType: f.mimeType, failed: false });
      })
      .catch(() => {
        if (!cancelled) setState({ src: null, mimeType: null, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, recordId, fieldName, playable]);
  return state;
}

export interface RecordMediaProps {
  tenantId: string;
  recordId: string;
  fieldName: string;
  className?: string;
  /** Hands the underlying <video> out so a SIBLING can seek it: a review
   *  marker reading "1:04" has to be able to set this element's currentTime.
   *  The audition screen passes one; TapeRecord does not. */
  videoRef?: RefObject<HTMLVideoElement>;
}

/** The tape itself. `playable` asks for the CV7 rendition — an Android
 * recording gets its AAC/mp4 sibling; an original that already plays gets
 * itself. `controls` and nothing clever: the browser's own player has the
 * scrubber, PiP and AirPlay, and a hand-rolled one would have none. */
export function RecordVideo({ tenantId, recordId, fieldName, className, videoRef }: RecordMediaProps) {
  const { src, failed } = useRecordFileUrl(tenantId, recordId, fieldName, true);
  if (failed) return <div class="rm rm--failed">The recording could not be reached.</div>;
  if (!src) return <div class="rm rm--loading" aria-hidden="true" />;
  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption -- the tape IS the content; captions live in the review transcript beside it
    <video
      ref={videoRef}
      class={`rm-video${className ? ` ${className}` : ''}`}
      src={src}
      controls
      playsInline
      preload="metadata"
    />
  );
}

export function RecordAudio({ tenantId, recordId, fieldName, className }: RecordMediaProps) {
  const { src, failed } = useRecordFileUrl(tenantId, recordId, fieldName, true);
  if (failed) return <div class="rm rm--failed">The recording could not be reached.</div>;
  if (!src) return <div class="rm rm--loading rm--loading-audio" aria-hidden="true" />;
  return <audio class={`rm-audio${className ? ` ${className}` : ''}`} src={src} controls preload="metadata" />;
}

export interface RecordImageProps extends RecordMediaProps {
  alt: string;
  /** The painted element, once the bytes are on screen — for callers that
   * need to ask the picture something (a tile measuring a logo's light). */
  onLoaded?: (img: HTMLImageElement) => void;
}

export function RecordImage({ tenantId, recordId, fieldName, alt, className, onLoaded }: RecordImageProps) {
  const { src, failed } = useRecordFileUrl(tenantId, recordId, fieldName, false);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  // The SECOND read can fail on its own (the bucket refusing CORS, the
  // presigned URL expiring, the network going) — and a swallowed failure
  // here left the loading shimmer up forever, which reads as "still coming"
  // about an image that never will.
  // Local to the <img>, distinct from the hook's `failed` (which means the URL
  // could not be RESOLVED). This one means the bytes would not RENDER.
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => setImgFailed(false), [src]);

  // ⚑ A PLAIN <img src>, NOT A FETCH-TO-BLOB. Changed 2026-09-20 after the
  // demo seed put real artwork on 9 project tiles and every one of them
  // rendered "This image could not be reached".
  //
  // The old path did `fetch(presignedUrl)` -> blob -> objectURL, and a
  // cross-origin **fetch** requires the bucket to answer with
  // Access-Control-Allow-Origin for this exact origin. A cross-origin
  // **<img src>** requires nothing at all — browsers have always loaded
  // third-party images without asking permission. So the blob round trip
  // bought us a hard dependency on a bucket CORS rule and gave nothing back:
  // the one thing it was for, KNOWING the image failed rather than leaving a
  // shimmer up forever, is exactly what `onError` reports.
  //
  // ⚠ The public artist page has always used a plain <img> and has always
  // worked (publicSubjectPage.ts). This component was the outlier, and the
  // symptom only surfaced off the deployed origin — which is why it survived
  // so long: the CORS rule covers app.calltowrap.com, so nobody testing
  // locally, and no suite, could see it.
  //
  // Same-origin `/records/...` rides the session cookie on an <img> exactly
  // as it did on a fetch.
  if (failed || imgFailed) return <div class="rm rm--failed">This image could not be reached.</div>;
  if (!src) return <div class="rm rm--loading" aria-hidden="true" />;
  return (
    <img
      class={className}
      src={src}
      alt={alt}
      onError={() => setImgFailed(true)}
      onLoad={onLoaded ? (e) => onLoaded(e.currentTarget as HTMLImageElement) : undefined}
    />
  );
}

export interface RecordAnyMediaProps extends RecordMediaProps {
  /** Only rendered for an image (a <video>/<audio> tag has no alt); an empty
   * string is fine — RecordImage's own contract, unchanged. */
  alt?: string;
  onLoaded?: (img: HTMLImageElement) => void;
}

/** ⚑ THE DISPATCHER — asks the door what `fieldName` actually holds and
 * renders the ONE tag that mime type needs, instead of a caller guessing
 * from the field name or the screen it's on.
 *
 * Found 2026-09-20: TapeRecord.tsx called `<RecordVideo>` on
 * `original_file_ref` unconditionally, because every self-tape it used to
 * show was a video. The demo seed put a `kind: 'photo'` `aa_media` row on
 * the same field, HouseholdTapes.tsx lists every `aa_media` kind together
 * by design (its own header: "the row's shape follows its facts, not its
 * kind"), and a parent who opened that row got `<video src="a JPEG">` — a
 * player that can never play, on a screen that never said why.
 *
 * One extra read (playable=true unconditionally) versus RecordImage's own
 * playable=false: harmless for an image (resolvePlaybackTarget falls
 * straight through to the original file when no rendition exists) and
 * correct for a video/audio row, so one hook call serves every kind rather
 * than probing twice.
 *
 * An unrecognised mime type is named plainly rather than defaulted to any
 * tag — a caller asking for a picklist value nobody has seen yet should see
 * that stated, not a silently wrong player. */
export function RecordMedia({ tenantId, recordId, fieldName, className, videoRef, alt, onLoaded }: RecordAnyMediaProps) {
  const { src, mimeType, failed } = useRecordFileUrl(tenantId, recordId, fieldName, true);
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => setImgFailed(false), [src]);

  if (failed) return <div class="rm rm--failed">This file could not be reached.</div>;
  if (!src) return <div class="rm rm--loading" aria-hidden="true" />;

  const base = (mimeType ?? '').split(';')[0]!.trim().toLowerCase();

  if (base.startsWith('video/')) {
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption -- same as RecordVideo above
      <video
        ref={videoRef}
        class={`rm-video${className ? ` ${className}` : ''}`}
        src={src}
        controls
        playsInline
        preload="metadata"
      />
    );
  }
  if (base.startsWith('audio/')) {
    return <audio class={`rm-audio${className ? ` ${className}` : ''}`} src={src} controls preload="metadata" />;
  }
  if (base.startsWith('image/')) {
    if (imgFailed) return <div class="rm rm--failed">This image could not be reached.</div>;
    // ⚑ `rm-img`, always — the video branch has always had `rm-video` (width
    // 100%, max-height 70vh) and this branch had NOTHING, so a 1545×2000
    // headshot opened from Materials rendered at its natural size inside a
    // page that could not scroll (Huan, the demo, 2026-09-27). A caller's own
    // class is added, never substituted, exactly like the video branch.
    return (
      <img
        class={`rm-img${className ? ` ${className}` : ''}`}
        src={src}
        alt={alt ?? ''}
        onError={() => setImgFailed(true)}
        onLoad={onLoaded ? (e) => onLoaded(e.currentTarget as HTMLImageElement) : undefined}
      />
    );
  }
  // Honest, not guessed: a type this screen has never been told how to show
  // (a PDF résumé filed under the wrong field, say) says so rather than
  // rendering a tag that will only ever fail.
  return (
    <div class="rm rm--unknown">
      {base ? `This is a ${base} file — there's no player for it here.` : "This file's type isn't known."}
    </div>
  );
}
